import { type SelectOption } from '@dm/core/types';
import { DiskTypeE } from 'app/common/constants/disk-type';

export enum RaidLevelE {
    RAID_10 = 1,
    RAID_5 = 2,
    RAID_0 = 3,
    RAID_1 = 4,
    RAID_6 = 5,
    RAID_50 = 6,
    RAID_3 = 7,
    RAID_TP = 11
}

export enum HotSpare {
    // 无盘
    none = 3,
    // 0盘
    zero = 4,
    // 1盘
    one = 1,
    // 8盘
    eight = 8
}

// 底层返回无效值4294967295
export const INVALID_NUM = '4294967295';

export const raidLevelList: SelectOption[] = [
    {
        value: RaidLevelE.RAID_10,
        label: 'RAID 10',
    },
    {
        value: RaidLevelE.RAID_5,
        label: 'RAID 5',
    },
    {
        value: RaidLevelE.RAID_6,
        label: 'RAID 6',
    },
    {
        value: RaidLevelE.RAID_TP,
        label: 'RAID-TP',
    },
    {
        value: RaidLevelE.RAID_0,
        label: 'RAID 0',
    },
    {
        value: RaidLevelE.RAID_1,
        label: 'RAID 1',
    },
    {
        value: RaidLevelE.RAID_50,
        label: 'RAID 50',
    },
    {
        value: RaidLevelE.RAID_3,
        label: 'RAID 3',
    },
];

/**
 * 容量硬盘
 */
export interface CapacityDisk {
    /**
     * 硬盘类型
     */
    diskType: string;
    /**
     * 硬盘类型枚举
     */
    diskTypeValue?: string;
    /**
     * 硬盘容量
     */
    cap: string;
    /**
     * 硬盘数量
     */
    diskNum: string;
    /**
     * 该类型的盘所属硬盘框信息
     */
    enclosureInfo?: EnclosureInfo[];
    /**
     * 是否是缓存盘
     */
    isCacheDisk: boolean;
}

/**
 * 引擎对象
 */
 export interface EngineObj {
    /**
     * 引擎对应盘列表
     * engine格式是 'EngineId: number;Enclosure:CTEnumber'
     */
    [engine: string]: CapacityDisk[];
}

/**
 * 引擎信息
 */
export interface EngineInfo {
    /**
     * 空闲盘信息
     */
    freeDisk?: EngineObj[];
    /**
     * 成员盘信息
     */
    memberDisk?: EngineObj[];
    /**
     * 是否是二级存储混插
     */
    isCacheVer?: boolean;
    smartPoolNum: number;
    smartPoolId: number;
}

/**
 * 硬盘框信息
 */
export interface EnclosureInfo {
    /**
     * 硬盘框ID
     */
    id?: string;
    /**
     * 硬盘框名称
     */
    name?: string;
    /**
     * 硬盘框有上多少块该容量的硬盘
     */
    count?: string;
}

/**
 * 选择的硬盘
 */
export interface SelectedDisk extends Disk {
    SECTORSIZE?: string;
    PARENTNAME?: string;
    engine?: string;
    visibleAreaId?: string;
}

/**
 * 处理后的engine信息
 */
export interface EngineData {
    /**
     * 硬盘容量
     */
    diskCap?: number;
    /**
     * 硬盘类型
     */
    diskType?: string;
    /**
     * 硬盘数量
     */
    diskNum?: number;
    /**
     * 共享域id
     */
    engineId?: string;
    /**
     * 共享域名称：控制框名称用,链接
     */
    engine?: string;
    /**
     * 嵌套结构时接收硬盘信息，表格结构时没有
     */
    diskData?: CapacityDisk[];
    /**
     * 引擎对应的硬盘框列表
     */
    diskEnclosure?: DiskEnclosure[];
}

// 处理后的硬盘信息，包括可用数量，已选数量等属性
export interface DiskData {
    /**
     * 盘唯一表示，类型-容量
     */
    key?: string;
    /**
     * 容量
     */
    cap?: number;
    /**
     * 类型
     */
    diskType?: string;
    /**
     * 用于显示类型(可能多个类型叠加显示：SAS+NL-SAS)
     */
    typeText?: string;
    /**
     * 用于标记硬盘类型(可能多个类型叠加显示：SAS+NL-SAS)
     */
    showType?: string;
    /**
     * Tier级别
     */
    tier?: TierE;
    /**
     * 已选引擎/硬盘框的可用硬盘总数
     */
    totalDiskNum?: number;
    /**
     * 每个引擎/硬盘框的可选硬盘数量
     * 是所有引擎/硬盘框中的最小可用盘数
     */
    availableDiskNum?: number;
    /**
     * 可输入的最大硬盘数
     */
    maxInputDiskNum?: number;
    /**
     * 可输入的最小硬盘数
     */
    minInputDiskNum?: number;
    /**
     * 输入框输入的硬盘数
     */
    inputDiskNum?: number;
    /**
     * 已有成员盘数
     */
    memberDiskNum?: number;
    /**
     * 最终已选硬盘数量
     */
    selectedDiskNum?: number;
    /**
     * 通过选盘形式选中的盘
     */
    selection: SelectedDisk[];
    /**
     * 盘所属硬盘框信息
     */
    enclosureInfo?: EnclosureInfo[];
    /**
     * 盘所属的引擎信息
     */
    engineInfo?: {
        [engineId: string]: {
            /**
             * 引擎名称
             */
            engine: string;
            /**
             * 可用硬盘数量
             */
            diskNum: number;
            /**
             * 成员硬盘数量
             */
            memberDiskNum: number;
            /**
             * 成员硬盘类型
             */
            diskType?: string;
        };
    };
    /**
     * 报错信息
     */
    errorMsg?: string;
    /**
     * 是否是缓存盘
     */
    isCacheDisk?: boolean;
    /**
     * 所属引擎
     */
    engine?: string;
    /**
     * 硬盘类型枚举数组
     */
    diskTypeValueList?: number[];
}

/**
 * 硬盘框数据
 */
export interface DiskEnclosure {
    /**
     * 硬盘框ID
     */
    id?: string;
    /**
     * 硬盘框名称
     */
    name?: string;
    /**
     * 共享域ID
     */
    engineId?: string;
    /**
     * 共享域名称，多个控制框用,分隔
     */
    engine?: string;
    /**
     * 硬盘框上的硬盘
     */
    disks?: {
        /**
         * 硬盘框有上多少块该容量的硬盘
         */
        count?: string;
        /**
         * 硬盘容量
         */
        cap?: string;
        /**
         * 硬盘类型
         */
        diskType?: string;
        /**
         * 展示硬盘框上有的硬盘类型
         */
        showType?: Array<string>;
    }[];
}

/**
 * 控制器下硬盘信息
 */
export interface CapInfo {
    cap: number;
    mirrorCap: number;
    num: number;
    recommend: boolean;
}

/**
 * 定义Tier层级
 */
export enum TierE {
    Performance = '0',
    Capacity = '1'
}

/**
 * 定义Tier层级对应的硬盘类型
 * 当前支持性能层、容量层
 */
export const TierLevel = {
    [TierE.Performance]: [DiskTypeE.SCM, DiskTypeE.SCM_SED],
    [TierE.Capacity]: [DiskTypeE.SSD, DiskTypeE.SSD_SED, DiskTypeE.NVME_SSD, DiskTypeE.NVME_SSD_SED,
    DiskTypeE.SAS, DiskTypeE.SATA, DiskTypeE.NL_SAS, DiskTypeE.SAS_SED, DiskTypeE.SATA_SED, DiskTypeE.NL_SAS_SED,
    DiskTypeE.CAPACITY_OPTIMIZED_SSD, DiskTypeE.CAPACITY_OPTIMIZED_SSD_SED, DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD,
    DiskTypeE.PHONY_CAPACITY_OPTIMIZED_SSD_SED],
};

/**
 * 硬盘类型、容量、数量等信息汇总
 */
export interface DiskTypeAndCap {
    /**
     * 硬盘所属Tier层
     */
    [tier: string]: {
        /**
         * 硬盘类型
         */
        [type: string]: {
            /**
             * 硬盘容量、硬盘数量
             */
            [cap: string]: number;
        };
    };
}

/**
 * 变化后emit的硬盘数据
 */
export interface ChangedDiskData {
    /** 是否校验通过 */
    valid: boolean;
    /** 加密盘数量 */
    encryptDiskCount: number;
    /** 已选硬盘 */
    selectedDiskArr: DiskData[];
    /** 已选控制框下所有硬盘 */
    diskDataArr: DiskData[];
    /** 已选控制框 */
    selectedEngineArr: EngineData[];
    /** 选择的硬盘类型 */
    selectType: '' | 'byId' | 'byNumber';
    /** 可用容量 */
    availableCapacity: number;
    /** 已有控制框信息 */
    memberEngineArr: EngineData[];
    /** 已选硬盘数量 */
    selectedDiskCount: number;
}
